import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import decodeToken from "../../lib/decodeToken";
import { calenderCheck } from "../../Store/apiStore";
import {
  advanceFromKey,
  isAddonFlowActive,
  backFromKey,
  addonDialogReturnPath,
  markAddonOAuthReturn,
  consumeAddonOAuthReturn,
} from "../../lib/addonIntroFlow";
import useStepBackGuard from "../../hooks/useStepBackGuard";
import styles from "./CalendarBookingIntro.module.css";
import AnimatedButton from "../AnimatedButton/AnimatedButton";

/*
 * CalendarBookingIntro — "Let {Agent} book your meetings" (Calendar).
 *
 * Ditto design of ScreenCallsIntro, calendar copy. Reached from the
 * SophiaReadyDialog "Connect your calendar" card (and from the contact
 * intro's Skip).
 *
 * CTAs:
 *   - Connect Calendar → POST /connect to start the Google Calendar
 *     OAuth round-trip (same contract as ConnectCalenderV2). On RN it
 *     hands the auth URL to the native shell; on web it redirects.
 *   - Skip — I'll set this up later → /screen-your-calls (the contact
 *     intro nudge).
 */

const readAgentCtx = () => {
  try {
    const raw = sessionStorage.getItem("agentDetails");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const Check = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3.5 8L6.5 11L12.5 5"
      stroke="#fff"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LockIcon = () => (
 <svg width="45" height="45" viewBox="0 0 45 45" fill="none" xmlns="http://www.w3.org/2000/svg">
<circle cx="22.5" cy="22.5" r="22" fill="#FDE7D7" stroke="white"/>
<path d="M26.5567 20.2746V16.4607C26.5567 14.0078 24.4519 12.0117 21.8651 12.0117C19.2783 12.0117 17.1734 14.0077 17.1734 16.4607V20.2746C15.6954 20.2746 14.4922 21.4155 14.4922 22.8172V27.9013C14.4922 29.3029 15.6953 30.4439 17.1734 30.4439H26.5567C28.0347 30.4439 29.238 29.303 29.238 27.9013V22.8172C29.238 21.4156 28.0348 20.2746 26.5567 20.2746ZM22.5348 26.63C22.5348 26.9793 22.2333 27.2652 21.865 27.2652C21.4966 27.2652 21.1951 26.9793 21.1951 26.63V24.0874C21.1951 23.7381 21.4966 23.4522 21.865 23.4522C22.2333 23.4522 22.5348 23.7381 22.5348 24.0874V26.63ZM25.2161 20.2746H18.5141V16.4607C18.5141 14.7098 20.0187 13.283 21.8651 13.283C23.7114 13.283 25.2161 14.7098 25.2161 16.4607V20.2746Z" fill="#FF6801"/>
</svg>

);

export default function CalendarBookingIntro() {
  const navigate = useNavigate();
  const location = useLocation();
  /*
   * Isolated entry: this screen was opened from a single-feature surface
   * (/setup-ready's card, or the Setup Summary popup on the dashboard) rather
   * than as a step of the onboarding add-on sequence. In that case EVERY exit —
   * back, skip, and a finished connect — returns to the entry point instead of
   * advancing the sequence.
   *
   * Whitelisted literals only: `returnTo` reaches navigate() directly, so an
   * arbitrary value would be an open redirect. The sessionStorage mirror covers
   * the OAuth round-trip, which remounts this screen with router state lost.
   */
  const ISOLATED_RETURN_ROUTES = ["/setup-ready", "/fast-agent-detail"];
  const isolatedReturnTo = (() => {
    const fromState = location.state?.returnTo;
    if (ISOLATED_RETURN_ROUTES.includes(fromState)) return fromState;
    try {
      const mirrored = sessionStorage.getItem("setupSummary.returnTo");
      if (ISOLATED_RETURN_ROUTES.includes(mirrored)) return mirrored;
    } catch (_e) {
      /* storage unavailable — router state above is the only source */
    }
    return null;
  })();
  const [connecting, setConnecting] = useState(false);

  /*
   * ── Already-done self-skip ──────────────────────────────────────
   *
   * Same rule as ContactSyncStep: an add-on screen whose feature is already
   * satisfied advances the sequence instead of asking again. The sequence
   * itself is a static list (TriageStep always passes ["contact","calendar"]),
   * so without this a second pass replays a calendar that is already connected.
   *
   * Judged with this screen's OWN source of truth — calenderCheck — rather
   * than a shared "is done" registry, so the two screens cannot drift apart.
   *
   * Guarded on `isAddonFlowActive()`: outside a sequence there is nothing to
   * advance to, and advanceFromKey() would clear the flow and bounce the user
   * to the dashboard — so a direct visit to /calendar-access still renders.
   * Isolated entries (Setup Ready card / Setup Summary popup) are also
   * excluded: the user picked that feature deliberately, so showing it is
   * correct even when connected.
   */
  useEffect(() => {
    if (isolatedReturnTo || !isAddonFlowActive()) return;
    let cancelled = false;
    (async () => {
      try {
        const userId = decodeToken(localStorage.getItem("token") || "")?.id;
        if (!userId) return;
        const res = await calenderCheck(userId);
        if (cancelled) return;
        if (res?.data?.connected) {
          navigate(advanceFromKey("calendar"), { replace: true });
        }
      } catch (_e) {
        // Can't tell → render the screen. Asking again is the safe failure.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [error, setError] = useState("");

  const ctx = readAgentCtx();
  const agentName = ctx?.agent?.agentName || "Sophia";

  const handleConnect = async () => {
    if (connecting) return;
    // Team member: the connect below is the ACCOUNT-level one for the logged-in
    // user — it would link the member's own account, not the owner's agent.
    // Send them to the connect screen, which applies the owner/admin rules for
    // this agent.
    if (localStorage.getItem("teamOwnerId")) {
      const memberAgentId =
        new URLSearchParams(location.search).get("agentId") ||
        sessionStorage.getItem("SelectAgentId") ||
        "";
      navigate("/connect-calender2", {
        state: memberAgentId ? { agentId: memberAgentId } : undefined,
      });
      return;
    }
    setConnecting(true);
    setError("");
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem("token") || "";
      const userId = decodeToken(token)?.id;
      const isRN = !!window.ReactNativeWebView;

      // agentId from the route query (?agentId=...) or the onboarding
      // sessionStorage cache. Passing it lets the backend callback
      // auto-provision (link) the calendar to this agent.
      const agentId =
        new URLSearchParams(location.search).get("agentId") ||
        sessionStorage.getItem("SelectAgentId") ||
        ctx?.agentId ||
        ctx?.agent?.agent_id ||
        "";

      // returnTo "/go-live-setup" is the whitelisted return path the
      // backend allows; GoLiveWizard resumes the add-on sequence there
      // (→ Contact screen if still queued, else dashboard). Mark the
      // OAuth round-trip so the wizard knows to resume rather than
      // render itself.
      /*
       * Isolated entry (Setup Ready card / Setup Summary popup): a finished
       * connect must land back where the user came from, NOT resume the
       * onboarding add-on sequence. So we skip the resume marker and send the
       * entry point as the OAuth return path.
       *
       * `/fast-agent-detail` is already in the backend's ALLOWED_RETURN_PATHS
       * (googleAuth.js) alongside `/go-live-setup`, so no backend change is
       * needed — the old code simply hardcoded the wizard path for every
       * caller. An unwhitelisted value is dropped server-side, which would
       * silently strand the user, hence the same whitelist on this side.
       */
      if (!isolatedReturnTo) markAddonOAuthReturn();
      const res = await axios.post(`${API_BASE_URL}/connect`, {
        userId,
        isMobile: isRN,
        ...(agentId ? { agentId } : {}),
        returnTo: isolatedReturnTo || "/go-live-setup",
        // [FIX: origin-redirect] — do frontend (3000/3001, live app.rexpt.in/
        // app.rxpt.ai) me callback wapas usi frontend pe laane ke liye origin bhejo.
        // REVERT: neeche wali `origin:` line hata do.
        origin: window.location.origin,
      });

      const authUrl = res?.data?.authUrl;
      if (authUrl) {
        if (isRN) {
          // Inside the RN WebView a plain location.href won't open
          // Google OAuth — hand the URL to native (same OPEN_GOOGLE_AUTH
          // contract Contacts / ConnectCalenderV2 use; flow:"calendar").
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              type: "OPEN_GOOGLE_AUTH",
              authUrl,
              flow: "calendar",
            })
          );
        } else {
          window.location.href = authUrl;
        }
        return;
      }
      // No authUrl — abort the resume marker so a later screen doesn't
      // wrongly think an OAuth round-trip is in flight.
      consumeAddonOAuthReturn();
      setError("Couldn't start the calendar connection. Please try again.");
      setConnecting(false);
    } catch (err) {
      console.error("[CalendarBookingIntro] connect failed:", err);
      consumeAddonOAuthReturn();
      setError("Couldn't connect your calendar. Please try again.");
      setConnecting(false);
    }
  };

  // Skip advances the sequence FROM the calendar screen: next screen
  // (contact) or, if calendar is last, the dashboard. Non-mutating —
  // browser Back can re-land here and advance again correctly.
  const handleSkip = () => {
    /*
     * Isolated entry: Skip is an EXIT, not a sequence advance. Previously this
     * always called advanceFromKey(), so skipping the calendar after opening it
     * from the Setup Summary walked the user into the next add-on screen
     * instead of back to the dashboard they came from.
     */
    if (isolatedReturnTo) {
      navigate(isolatedReturnTo, { replace: true });
      return;
    }
    navigate(advanceFromKey("calendar"));
  };

  // Device/browser back walks the sequence backward (→ previous screen),
  // or, when calendar is the FIRST screen, returns to the "Sophia is
  // live" add-ons popup on /go-live-setup. Never escapes to Stripe.
  useStepBackGuard({
    // Isolated entry has no sequence to walk backward through — back is an
    // exit, straight to the surface that opened this screen. Without this,
    // a calendar that is not FIRST in the add-on sequence would send the user
    // to the previous add-on screen instead of back to the dashboard.
    canGoBack: () => !isolatedReturnTo && backFromKey("calendar") !== null,
    onBack: () => navigate(backFromKey("calendar")),
    onExit: () => navigate(
      isolatedReturnTo || addonDialogReturnPath(),
      isolatedReturnTo ? { replace: true } : undefined,
    ),
  });

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <h1 className={styles.title}>
          <span className={styles.titleLight}>Let {agentName}</span>
          <span className={styles.titleBold}>book your meetings</span>
        </h1>

        <p className={styles.subtitle}>
          Connect your calendar so {agentName} can{" "}
          <span className={styles.subtitleStrong}>book meetings for you</span>{" "}
          right on the call — no double-bookings, no back-and-forth.
        </p>

        <div className={styles.howCard}>
          <h2 className={styles.howTitle}>How it works:</h2>
          <ul className={styles.howList}>
            <li className={styles.howItem}>
              <span className={styles.checkBadge}>
                <Check />
              </span>
              Checks your free slots before booking
            </li>
            <li className={styles.howItem}>
              <span className={styles.checkBadge}>
                <Check />
              </span>
              Books meetings directly during the call
            </li>
            <li className={styles.howItem}>
              <span className={styles.checkBadge}>
                <Check />
              </span>
              Adds every appointment straight to your calendar
            </li>
          </ul>
        </div>

        <div className={styles.privacyChip}>
          <span className={styles.privacyIcon}>
            <LockIcon />
          </span>
          <span className={styles.privacyText}>
            <b className={styles.privacyTextBold}>
              {agentName} only adds and checks appointment times
            </b>{" "}
            to book your meetings. She never reads your personal events for
            anything else.
          </span>
        </div>

        {error && <p className={styles.error}>{error}</p>}
      </div>

      <div className={styles.footer}>
        <AnimatedButton
          label={
            connecting ? (
              "Connecting…"
            ) : (
              <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                Connect Calendar
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M7.33944 13.093L5.94953 11.7166L10.1125 7.55362H-0.00146484V5.54297H10.1125L5.94953 1.38672L7.33944 0.00355083L13.8842 6.5483L7.33944 13.093Z" fill="white" />
                </svg>
              </span>
            )
          }
          onClick={handleConnect}
          disabled={connecting}
          positionType="relative"
          width="100%"
          bottom="0px"
        />
        <button type="button" className={styles.skipLink} onClick={handleSkip}>
          Skip — I'll set this up later
        </button>
      </div>
    </div>
  );
}
